import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useScopedTranslate } from '@locale/translate';

import { translationFor } from 'helpers/translations';
import { useStore } from 'models/Provider';

import {
  createWorkType,
  deleteWorkType,
  loadWorkTypes,
  Translation,
  updateWorkType,
  WorkType,
} from './requests';

export const useWorkTypes = (lang?: string) => {
  const { current } = useStore();
  const { t } = useScopedTranslate(locale);
  const language = lang ?? current.info.defaultLanguage;
  return useQuery({
    queryKey: ['work-types'],
    queryFn: loadWorkTypes,
    select: useCallback(
      (data: WorkType[]) => {
        const results = data.map((workType) => ({
          ...workType,
          title: translationFor(workType.translations, language)?.title ?? t('noTranslation'),
        }));
        return results;
      },
      [language, t],
    ),
  });
};

type WorkTypeError = AxiosError<{ translations: { language: string; message: string } }>;

export const useCreateWorkTypes = (props: {
  onSuccess?: (workTypeId: number) => void;
  setLanguageError?: ({ language, message }: { language: string; message: string }) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { enabled: boolean; translations: Translation[] }) =>
      createWorkType({ translations: data.translations, enabled: data.enabled }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['work-types']);
        props.onSuccess?.(data.id);
      },
      onError: (error: WorkTypeError) => {
        if (error && error.response && error.response.data) {
          props.setLanguageError && props.setLanguageError(error.response.data.translations);
        }
      },
    },
  );
};

export const useUpdateWorkTypes = (props: {
  onSuccess?: (workTypeId: number) => void;
  setLanguageError?: ({ language, message }: { language: string; message: string }) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { id: number; enabled: boolean; translations: Translation[] }) =>
      updateWorkType(data.id, { translations: data.translations, enabled: data.enabled }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['work-types']);
        props.onSuccess?.(data.id);
      },
      onError: (error: WorkTypeError) => {
        if (error && error.response && error.response.data) {
          props.setLanguageError &&
            props.setLanguageError({
              language: error.response.data.translations.language,
              message: error.response.data.translations.message,
            });
        }
      },
    },
  );
};

export const useDeleteWorkTypes = (props: { id: number; onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  return useMutation(() => deleteWorkType(props.id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['work-types']);
      props?.onSuccess?.();
    },
  });
};

const locale = {
  en: {
    noTranslation: 'Missing translation',
  },
  is: {
    noTranslation: 'Vantar þýðingu',
  },
};
